"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const PRESETS = [5, 10, 15, 20];

/**
 * Objectif du jour (discret) : anneau de progression + minutes du jour vs
 * rythme choisi. Toutes activités confondues (formations, quiz, Reps, appels).
 * Placé dans l'en-tête du dashboard, aligné avec le bloc de bienvenue.
 * - Desktop : cluster compact inline à droite de la salutation (sans surface).
 * - Mobile : ancré dans une surface douce (à la Uxcel : fond neutre léger,
 *   arrondi, sans bordure) pour se lire comme un composant intentionnel.
 * Masqué si aucun rythme n'est défini (pas de nag). Rythme modifiable via le
 * menu réglage (avec un rappel de ce qui est comptabilisé).
 */
export default function DailyGoalWidget({ dailyGoal }) {
    const t = useTranslations("dashboard");

    // Objectif géré en local (optimiste), seedé depuis le payload serveur.
    const [goal, setGoal] = useState(dailyGoal?.goalMinutes || null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        setGoal(dailyGoal?.goalMinutes || null);
    }, [dailyGoal?.goalMinutes]);

    // Fermer le menu au clic à l'extérieur
    useEffect(() => {
        if (!editing) return;
        const handleClick = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setEditing(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [editing]);

    if (!goal) return null;

    const minutesToday = Math.max(0, dailyGoal?.minutesToday || 0);
    const reached = minutesToday >= goal;
    const percent = Math.min(100, Math.round((minutesToday / goal) * 100));

    // Anneau de progression (SVG), compact
    const size = 38;
    const stroke = 4;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference * (1 - percent / 100);

    const handlePickGoal = async (minutes) => {
        setEditing(false);
        if (minutes === goal) return;
        const previous = goal;
        setGoal(minutes); // optimiste
        setSaving(true);
        try {
            const response = await fetch("/api/user/update-daily-goal", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ dailyGoal: minutes }),
            });
            if (!response.ok) setGoal(previous); // rollback
        } catch (error) {
            setGoal(previous);
        }
        setSaving(false);
    };

    return (
        <div
            ref={containerRef}
            className="relative flex w-full items-center justify-between gap-3 rounded-2xl bg-gray-50 p-4 dark:bg-white/[0.04] sm:w-auto sm:shrink-0 sm:justify-start sm:rounded-none sm:bg-transparent sm:p-0 sm:dark:bg-transparent"
        >
            <div className="flex items-center gap-3 min-w-0">
                {/* Anneau (%) */}
                <div
                    className="relative shrink-0"
                    style={{ width: size, height: size }}
                >
                    <svg width={size} height={size} className="-rotate-90">
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            strokeWidth={stroke}
                            className="stroke-gray-200 dark:stroke-grayDark-700"
                        />
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            strokeWidth={stroke}
                            strokeLinecap="round"
                            className={`transition-[stroke-dashoffset] duration-700 ease-out ${
                                reached
                                    ? "stroke-emerald-500"
                                    : "stroke-gray-400 dark:stroke-grayDark-500"
                            }`}
                            style={{
                                strokeDasharray: circumference,
                                strokeDashoffset: dashOffset,
                            }}
                        />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                        {reached ? (
                            <SolarIcon
                                name="check-circle-bold"
                                className="w-5 h-5 text-emerald-500"
                            />
                        ) : (
                            <span className="text-[11px] font-semibold text-gray-500 dark:text-grayDark-400">
                                {percent}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Texte compact */}
                <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-grayDark-500 mb-0 whitespace-nowrap">
                        {t("daily_goal_title")}
                    </p>
                    <p className="text-[13px] font-semibold text-gray-600 dark:text-grayDark-300 mb-0 whitespace-nowrap">
                        {t("daily_goal_amount", {
                            minutes: minutesToday,
                            goal: goal,
                        })}
                    </p>
                </div>
            </div>

            {/* Réglage du rythme */}
            <button
                type="button"
                onClick={() => setEditing((v) => !v)}
                aria-label={t("daily_goal_edit")}
                className="flex shrink-0 text-gray-300 transition-colors hover:text-gray-500 dark:text-grayDark-500 dark:hover:text-grayDark-300"
            >
                <SolarIcon name="pen-2-linear" className="w-3.5 h-3.5" />
            </button>

            {editing && (
                <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-borderDark dark:bg-bgDark-840">
                    <p className="px-1.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                        {t("daily_goal_edit_title")}
                    </p>
                    <div className="flex flex-col gap-1">
                        {PRESETS.map((minutes) => {
                            const isCurrent = minutes === goal;
                            return (
                                <button
                                    key={minutes}
                                    type="button"
                                    disabled={saving}
                                    onClick={() => handlePickGoal(minutes)}
                                    className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${
                                        isCurrent
                                            ? "bg-emerald-50 font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                                            : "text-gray-600 hover:bg-gray-50 dark:text-grayDark-300 dark:hover:bg-white/10"
                                    }`}
                                >
                                    <span>
                                        {t("daily_goal_minutes", { minutes })}
                                    </span>
                                    {isCurrent && (
                                        <SolarIcon
                                            name="check-circle-bold"
                                            className="w-4 h-4"
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <p className="mt-1 border-t border-gray-100 px-1.5 pt-2 text-[11px] leading-relaxed text-gray-400 dark:border-white/10 dark:text-grayDark-400 mb-0">
                        {t("daily_goal_info_body")}
                    </p>
                </div>
            )}
        </div>
    );
}
